import { useState, useRef, useCallback, type KeyboardEvent as ReactKeyboardEvent } from "react";
interface BeforeAfterSliderProps {
  before: string;
  after: string;
  label?: string;
  detail?: string;
}
export function BeforeAfterSlider({
  before,
  after,
  label,
  detail
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPosition(Math.round(x / rect.width * 100));
  }, []);
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons !== 1) return;
    updateFromClientX(e.clientX);
  };
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") setPosition(p => Math.max(0, p - 2));
    if (e.key === "ArrowRight") setPosition(p => Math.min(100, p + 2));
    if (e.key === "Home") setPosition(0);
    if (e.key === "End") setPosition(100);
  };
  const showBefore = position <= 50;
  return <div className="overflow-hidden rounded-[2rem] bg-white shadow-soft ring-1 ring-[#A9DFE9]/60" data-id="element-14" data-mp="1ncbqhti82e9h">
      <div ref={containerRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} className="relative aspect-[4/5] w-full select-none touch-none cursor-ew-resize" role="slider" aria-label="Drag to compare before and after" aria-valuemin={0} aria-valuemax={100} aria-valuenow={position} aria-valuetext={`${position}% revealing the after image`} tabIndex={0} onKeyDown={handleKeyDown} data-id="element-15" data-mp="1s9ahw4ai473z">
        {/* 1) Base: imagen "Before" — siempre detrás, ocupa toda el área */}
        <img src={before} alt={`${label ?? "Case"} before`} className="absolute inset-0 h-full w-full object-cover" draggable={false} data-id="element-16" data-mp="3vxiyrgqmlc9" />

        {/* 2) Overlay: imagen "After Day 5" — anclada a la derecha, crece desde position% */}
        <div className="absolute top-0 right-0 bottom-0 overflow-hidden" style={{
        width: `${position}%`
      }} data-id="element-17" data-mp="8tfv0qq1xgoh">
          <img src={after} alt={`${label ?? "Case"} after`} className="h-full w-full object-cover" draggable={false} data-id="element-18" data-mp="ms7767lcss9z" />
        </div>

        {/* 3) Línea divisoria */}
        <div className="pointer-events-none absolute top-0 bottom-0 w-px bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.25)]" style={{
        left: `${position}%`
      }} data-id="element-19" data-mp="wle9tkoplu6d" />

        {/* 4) Handle arrastrable */}
        <div className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{
        left: `${position}%`
      }} data-id="element-20" data-mp="11iwkpgmaejh0">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 shadow-[0_8px_24px_rgba(0,0,0,0.2)] ring-1 ring-[#A9DFE9]/60" data-id="element-21" data-mp="1l57y7u46ygx9">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-[#1A1A2E]" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-22" data-mp="1b71t73n38uo6">
              <polyline points="15 18 9 12 15 6" data-id="element-23" data-mp="167z1ekw4cky5" />
              <polyline points="9 6 15 12 9 18" transform="translate(6,0)" data-id="element-24" data-mp="sfr85341nc47" />
            </svg>
          </div>
        </div>

        {/* 5) Badge "Before" — esquina izquierda, sobre la imagen Before */}
        <span className={`pointer-events-none absolute left-4 top-4 rounded-full bg-[#1A1A2E]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white transition-opacity duration-300 ${showBefore ? "opacity-100" : "opacity-0"}`} aria-hidden={!showBefore} data-id="element-25" data-mp="ihl3idqurrvn">
          Before
        </span>

        {/* 6) Badge "After Day 5" — esquina derecha, sobre la imagen After */}
        <span className={`pointer-events-none absolute right-4 top-4 rounded-full bg-[#1A1A2E]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white transition-opacity duration-300 ${showBefore ? "opacity-0" : "opacity-100"}`} aria-hidden={showBefore} data-id="element-26" data-mp="1h0qqya4wa24y">
          After Day 5
        </span>
      </div>

      {(label || detail) && <div className="px-6 py-5" data-id="element-27" data-mp="y3pk0jrs5ryd">
          {label && <p className="text-sm font-semibold text-[#1A1A2E]" data-id="element-28" data-mp="t3tegg3fpsgp">{label}</p>}
          {detail && <p className="mt-1 text-xs leading-relaxed text-[#1A1A2E]/70" data-id="element-29" data-mp="t5xolfrxfnpf">{detail}</p>}
        </div>}
    </div>;
}